import { ShareDocletGatingData } from '../api'

/**
 * Publish & sharing rules for the builder's panel (see the development plan's
 * Phase 5). Gating is stored on the doclet; GatingSettings is the panel's
 * editable copy of it.
 */

export type GatingModality = ShareDocletGatingData['modality']

// Panel order, lowest friction first
export const GATING_MODALITIES: {
  modality: GatingModality
  friction: 'none' | 'low' | 'medium' | 'highest'
}[] = [
  { modality: 'direct', friction: 'none' },
  { modality: 'collectEmail', friction: 'low' },
  { modality: 'password', friction: 'medium' },
  { modality: 'invited', friction: 'highest' }
]

export interface GatingSettings {
  modality: GatingModality
  // For 'password'; shown in plain text, since the editor sends it on
  password: string
  // For 'invited': email addresses and "@domain" entries
  invitees: string[]
}

export const DEFAULT_GATING: GatingSettings = {
  modality: 'direct',
  password: '',
  invitees: []
}

// A domain name: dot-separated parts of letters, digits and inner hyphens,
// ending in a letters-only top-level domain (or an internationalised xn-- one)
const DOMAIN_NAME =
  '(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+(?:[a-z]{2,}|xn--[a-z0-9-]+)'
// Before the @: letters, digits and . _ % + - ', with dots only between them
const LOCAL_PART = "[a-z0-9_%+'-]+(?:\\.[a-z0-9_%+'-]+)*"
const EMAIL = new RegExp(`^${LOCAL_PART}@${DOMAIN_NAME}$`)
const DOMAIN = new RegExp(`^@${DOMAIN_NAME}$`)

/**
 * One invite list entry: an email address, or a whole domain written with its
 * @ ("@example.com"). The @ is required, so a mistyped address like
 * "sarah.chen" isn't taken for a domain. Lower-cased; null if it's neither.
 * The API (ShareDocletService/shared/gating.ts) applies the same rules.
 */
export const parseInvitee = (text: string): string | null => {
  const value = text.trim().toLowerCase()
  return EMAIL.test(value) || DOMAIN.test(value) ? value : null
}

/**
 * Entries as typed or pasted: separated by commas, semicolons, spaces or new
 * lines. Valid ones are cleaned and deduplicated; the rest are returned as
 * typed, so they can be shown and corrected.
 */
export const parseInvitees = (
  text: string
): { valid: string[]; invalid: string[] } => {
  const valid: string[] = []
  const invalid: string[] = []
  for (const entry of text.split(/[\s,;]+/)) {
    if (!entry) continue
    const invitee = parseInvitee(entry)
    if (!invitee) invalid.push(entry)
    else if (!valid.includes(invitee)) valid.push(invitee)
  }
  return { valid, invalid }
}

// The gating with these entries added to its invite list, skipping repeats
export const addInvitees = (
  gating: GatingSettings,
  invitees: string[]
): GatingSettings => {
  const added = invitees.filter(invitee => !gating.invitees.includes(invitee))
  return added.length
    ? { ...gating, invitees: [...gating.invitees, ...added] }
    : gating
}

export type GatingProblem = 'passwordRequired' | 'inviteesRequired'

// What stops these settings from being applied, if anything
export const gatingProblem = (gating: GatingSettings): GatingProblem | null => {
  if (gating.modality === 'password' && !gating.password.trim()) {
    return 'passwordRequired'
  }
  if (gating.modality === 'invited' && !gating.invitees.length) {
    return 'inviteesRequired'
  }
  return null
}

export type PublishAction = 'publish' | 'unpublish' | null

/**
 * What Done has to do to the doclet's publish state. Publishing again is how
 * a published doclet's latest edits go live, so it's only done when asked.
 */
export const publishActionFor = (
  doclet: { isPublished: boolean; hasUnpublishedChanges: boolean },
  wantPublished: boolean,
  publishLatestEdits: boolean
): PublishAction => {
  if (!wantPublished) return doclet.isPublished ? 'unpublish' : null
  if (!doclet.isPublished) return 'publish'
  return doclet.hasUnpublishedChanges && publishLatestEdits ? 'publish' : null
}

// The panel's copy of the doclet's stored gating
export const gatingFromDoclet = (
  gating: ShareDocletGatingData | null | undefined
): GatingSettings =>
  gating
    ? {
        modality: gating.modality,
        password: gating.password ?? '',
        invitees: gating.invitees
      }
    : DEFAULT_GATING

// What the API stores: every field, whatever the modality, so switching
// options and back restores what was entered
export const gatingInput = (gating: GatingSettings): ShareDocletGatingData => ({
  modality: gating.modality,
  password: gating.password.trim() ? gating.password : null,
  invitees: gating.invitees
})

export const sameGating = (a: GatingSettings, b: GatingSettings) =>
  a.modality === b.modality &&
  a.password === b.password &&
  a.invitees.length === b.invitees.length &&
  a.invitees.every((invitee, i) => invitee === b.invitees[i])

// Whether a gating change reaches people who already have the link
export const gatingChangeAffectsLink = (
  isPublished: boolean,
  saved: GatingSettings,
  next: GatingSettings
) =>
  isPublished &&
  (saved.modality !== next.modality ||
    (next.modality === 'password' && saved.password !== next.password))
